<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'NumberingSelector',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'input']);

  const props = defineProps({
    objectType: {
      type: String,
      required: true,
    },
    name: {
      type: String,
      required: false,
    },
    value: {
      type: null,
      required: true,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
    bindObject: {
      type: Boolean,
      required: false,
      default: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const internalValue = ref(null);
  const items = ref(null);
  const ux = ref([]);
  const b_7315092382398562_filterable = computed(() => false);
  const b_7315092382398562_searchable = computed(() => false);

  const $data = computed(() => ({
    internalValue: internalValue.value,
    items: items.value,
    ux: ux.value,
    b_7315092382398562_filterable: b_7315092382398562_filterable.value,
    b_7315092382398562_searchable: b_7315092382398562_searchable.value,
  }));

  const $$load_internalValue = async () => {
    return props.bindObject
      ? props.value
        ? props.value.id
        : null
      : props.value;
  };
  const $$load_items = async () => {
    return $fn.value
      .fetch('list_numbering', {
        parameters: { resource_type: props.objectType },
      })
      .then((d) =>
        [{ id: null, description: $fn.value.gettext('Manual') }].concat(d)
      );
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'internalValue' },
      { name: 'items' },
      { name: 'ux' },
      { name: 'b_7315092382398562_filterable', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_searchable', nodeId: '7315092382398562' },
    ],
    loaders: { internalValue: $$load_internalValue, items: $$load_items },
    ux: {},
    dataRefs: {
      internalValue,
      items,
      ux,
      b_7315092382398562_filterable,
      b_7315092382398562_searchable,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.objectType,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('objectType');
    }
  );
  watch(
    () => props.name,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('name');
    }
  );
  watch(
    () => props.value,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('value');
    }
  );
  watch(
    () => props.readonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readonly');
    }
  );
  watch(
    () => props.bindObject,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('bindObject');
    }
  );

  const act_7315092382398562_emit_1nm6drqgl_getActionArgs = async ($event) => {
    return {
      event: 'input',
      value: props.bindObject
        ? { id: $event.originalEvent }
        : $event.originalEvent,
    };
  };
  const act_3343864469950044_register_opy0iq1m3_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'resultID',
      value:
        $event.data.modalResult &&
        $event.data.modalResult.ok &&
        $event.data.modalResult.value.id,
    };
  };
  const inst_evh_3343864469950044_close_ejmad1i0t_getWhen = async ($event) => {
    return $event.data.resultID;
  };
  const act_3343864469950044_setData_kea4rjxwc_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'internalValue',
      value: resultID,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const inst_evh_3343864469950044_close_vsybkjenm_getWhen = async ($event) => {
    return $event.data.resultID;
  };
  const act_3343864469950044_showModal_yiaouikb1_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Numbering',
      props: {
        objectType: props.objectType,
      },
    };
  };

  const act_7315092382398562_emit_1nm6drqgl = new actionTypes.EmitAction({
    actionArgs: act_7315092382398562_emit_1nm6drqgl_getActionArgs,
  });
  const inst_evh_7315092382398562_input_u8yqn3i8o =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_emit_1nm6drqgl,
      event: 'input',
      displayName: 'emit',
    });
  const inst_evh_7315092382398562_input = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_input_u8yqn3i8o],
    event: 'input',
  });
  const act_3343864469950044_register_opy0iq1m3 =
    new actionTypes.RegisterAction({
      actionArgs: act_3343864469950044_register_opy0iq1m3_getActionArgs,
    });
  const inst_evh_3343864469950044_close_2zl6e3tkr =
    new actionTypes.EventHandlerImpl({
      action: act_3343864469950044_register_opy0iq1m3,
      event: 'close',
      displayName: 'register',
    });
  const act_3343864469950044_reloadComponentData_aab872r04 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_3343864469950044_close_ejmad1i0t =
    new actionTypes.EventHandlerImpl({
      action: act_3343864469950044_reloadComponentData_aab872r04,
      event: 'close',
      when: inst_evh_3343864469950044_close_ejmad1i0t_getWhen,
      displayName: 'reloadComponentData',
    });
  const act_3343864469950044_setData_kea4rjxwc = new actionTypes.SetDataAction({
    actionArgs: act_3343864469950044_setData_kea4rjxwc_getActionArgs,
  });
  const inst_evh_3343864469950044_close_vsybkjenm =
    new actionTypes.EventHandlerImpl({
      action: act_3343864469950044_setData_kea4rjxwc,
      event: 'close',
      when: inst_evh_3343864469950044_close_vsybkjenm_getWhen,
      displayName: 'setData',
    });
  const act_3343864469950044_showModal_yiaouikb1 =
    new actionTypes.ShowModalAction({
      actionArgs: act_3343864469950044_showModal_yiaouikb1_getActionArgs,
      events: [
        inst_evh_3343864469950044_close_2zl6e3tkr,
        inst_evh_3343864469950044_close_ejmad1i0t,
        inst_evh_3343864469950044_close_vsybkjenm,
      ],
    });
  const inst_evh_3343864469950044_click_9dmew4xt7 =
    new actionTypes.EventHandlerImpl({
      action: act_3343864469950044_showModal_yiaouikb1,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_3343864469950044_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3343864469950044_click_9dmew4xt7],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_input_u8yqn3i8o,
    inst_evh_7315092382398562_input,
    inst_evh_3343864469950044_close_2zl6e3tkr,
    inst_evh_3343864469950044_close_ejmad1i0t,
    inst_evh_3343864469950044_close_vsybkjenm,
    inst_evh_3343864469950044_click_9dmew4xt7,
    inst_evh_3343864469950044_click,
  };

  const evh_7315092382398562_input_u8yqn3i8o = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_input_u8yqn3i8o.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_input = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_input.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_close_2zl6e3tkr = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_close_2zl6e3tkr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_close_ejmad1i0t = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_close_ejmad1i0t.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_close_vsybkjenm = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_close_vsybkjenm.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_click_9dmew4xt7 = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_click_9dmew4xt7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_click = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpSelect2
    v-model="internalValue"
    :value="internalValue"
    @input="
      !($event && typeof $event.stopPropagation === 'function') &&
        (internalValue =
          $event && typeof $event === 'object' && 'data' in $event
            ? $event.data
            : $event);
      evh_7315092382398562_input($event, {});
    "
    :name="name"
    valueField="id"
    textField="description"
    :filterable="b_7315092382398562_filterable"
    :searchable="b_7315092382398562_searchable"
    :options="items"
    :readonly="readonly"
  >
    <template #list-footer>
      <hr />
      <ItpLink
        :text="gettext('Add new...')"
        class="dropdown-item"
        @click="evh_3343864469950044_click($event, {})"
      >
      </ItpLink>
    </template>
  </ItpSelect2>
</template>
